import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  Check,
  ClipboardList,
  Eye,
  LockKeyhole,
  MessageCircle,
  Repeat2,
  Send,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthContext, apiClient } from '../App';
import Footer from '../components/Footer';

const PAGE_TITLE = 'Basketball Training and Program Tools | OpenRuns';
const PAGE_DESCRIPTION = 'Move basketball training beyond the group chat with one link for schedules, registrations, rosters, waitlists, updates, and check-in.';
const PAGE_URL = 'https://openrunsusa.com/trainers';
const AUTH_ENTRY_PATH = '/?openAuth=1';
const TRAINER_FEATURES_HASH = '#trainer-features';

const scrollToTrainerFeatures = (behavior = 'smooth') => {
  document.getElementById('trainer-features')?.scrollIntoView({ behavior, block: 'start' });
};

export const hasTrainerStorefrontSetup = (storefront) => Boolean(
  storefront?.storefront_headline ||
  storefront?.storefront_about ||
  storefront?.storefront_athlete_levels?.length ||
  storefront?.storefront_ability_levels?.length
);

export const getTrainerAudienceCta = ({ user, storefront, loading }) => {
  if (!user) {
    return { label: 'See Trainer Features', to: TRAINER_FEATURES_HASH, description: null };
  }

  if (loading) {
    return {
      label: 'Checking Trainer Page…',
      to: null,
      description: 'Checking your current trainer-page setup and visibility.',
    };
  }

  if (!storefront) {
    return {
      label: 'View My Trainer Page',
      to: '/my-trainer-page',
      description: 'Open your trainer page to review its current setup and visibility.',
    };
  }

  if (storefront.public_visibility?.is_public) {
    return {
      label: 'Go to My Trainer Page',
      to: '/my-trainer-page',
      description: 'Your trainer page is public. Open the owner view to manage it.',
    };
  }

  if (!hasTrainerStorefrontSetup(storefront)) {
    return {
      label: 'Set Up My Trainer Page',
      to: '/my-trainer-page',
      description: 'Add storefront details in your owner preview. Public visibility still depends on the current eligibility rules.',
    };
  }

  const statusCopy = {
    private_profile: 'Your profile is private, so your trainer page is not publicly visible.',
    teen_not_public: 'You can preview your hosted activities, but public sharing is not available for this account.',
    needs_public_offering: 'Add a public offering before your trainer page can be viewed publicly.',
    otherwise_unavailable: 'Your trainer page is available as an owner preview, but public sharing is not available right now.',
  };

  return {
    label: 'View My Trainer Page',
    to: '/my-trainer-page',
    description: statusCopy[storefront.public_visibility?.status] ||
      'Open your owner preview to see the current public visibility status.',
  };
};

const CAPABILITIES = [
  {
    icon: CalendarDays,
    title: 'Publish a session players can understand',
    description: 'Publish, edit, or cancel a session while keeping its stored time, place, capacity, format, and approved location details on one page.',
  },
  {
    icon: Repeat2,
    title: 'Run next week without starting over',
    description: 'Create recurring weekly sessions or duplicate an upcoming session, then manage each one on its own.',
  },
  {
    icon: ClipboardList,
    title: 'Keep registrations organized',
    description: 'See who joined, who is waitlisted, who was invited, and the stored confirmation state.',
  },
  {
    icon: Send,
    title: 'Share one link that stays current',
    description: 'Use the canonical session page in group chats, social posts, and QR codes.',
  },
  {
    icon: MessageCircle,
    title: 'Update players without repeating yourself',
    description: 'Send factual, session-specific messages and keep schedule changes tied to the selected session.',
  },
  {
    icon: Users,
    title: 'Handle game day',
    description: 'View the roster, manage the waitlist, and check in eligible joined players.',
  },
];

const TrainerPrograms = () => {
  const { user } = useContext(AuthContext) || {};
  const [storefrontState, setStorefrontState] = useState({ loading: false, data: null });

  useEffect(() => {
    let current = true;

    if (!user) {
      setStorefrontState({ loading: false, data: null });
      return () => { current = false; };
    }

    setStorefrontState({ loading: true, data: null });
    apiClient.get('/me/trainer-storefront')
      .then((response) => {
        if (current) setStorefrontState({ loading: false, data: response.data });
      })
      .catch(() => {
        if (current) setStorefrontState({ loading: false, data: null });
      });

    return () => { current = false; };
  }, [user?.id]);

  const audienceCta = useMemo(() => getTrainerAudienceCta({
    user,
    storefront: storefrontState.data,
    loading: storefrontState.loading,
  }), [storefrontState.data, storefrontState.loading, user]);

  useEffect(() => {
    if (window.location.hash !== TRAINER_FEATURES_HASH) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => scrollToTrainerFeatures('auto'));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const previous = {
      description: description?.getAttribute('content'),
      ogTitle: ogTitle?.getAttribute('content'),
      ogDescription: ogDescription?.getAttribute('content'),
      ogUrl: ogUrl?.getAttribute('content'),
      canonical: canonical?.getAttribute('href'),
    };

    document.title = PAGE_TITLE;
    description?.setAttribute('content', PAGE_DESCRIPTION);
    ogTitle?.setAttribute('content', PAGE_TITLE);
    ogDescription?.setAttribute('content', PAGE_DESCRIPTION);
    ogUrl?.setAttribute('content', PAGE_URL);
    canonical?.setAttribute('href', PAGE_URL);

    return () => {
      document.title = previousTitle;
      if (previous.description) description?.setAttribute('content', previous.description);
      if (previous.ogTitle) ogTitle?.setAttribute('content', previous.ogTitle);
      if (previous.ogDescription) ogDescription?.setAttribute('content', previous.ogDescription);
      if (previous.ogUrl) ogUrl?.setAttribute('content', previous.ogUrl);
      if (previous.canonical) canonical?.setAttribute('href', previous.canonical);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      <header className="sticky top-0 z-50 border-b border-zinc-100 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" aria-label="OpenRuns home">
            <img src="/logo-transparent.png" alt="Open Runs USA" className="h-auto w-[112px] sm:w-[190px]" />
          </Link>
          <nav className="order-3 flex w-full items-center justify-center gap-6 text-sm font-semibold text-zinc-700 sm:order-2 sm:w-auto" aria-label="Audience navigation">
            <Link to="/#players" className="transition-colors hover:text-[#FF6A00]">Players</Link>
            <span className="text-[#FF6A00]" aria-current="page">Trainers</span>
            <Link to="/#hosts" className="transition-colors hover:text-[#FF6A00]">Hosts</Link>
          </nav>
          <div className="order-2 flex items-center gap-2 sm:order-3">
            <Button asChild variant="outline" className="h-9 rounded-xl border-orange-200 px-3 text-xs font-semibold text-[#C44F00] sm:h-10 sm:px-4 sm:text-sm">
              <Link to={AUTH_ENTRY_PATH}>Login</Link>
            </Button>
            <Button asChild className="h-9 rounded-xl bg-[#FF6A00] px-3 text-xs font-semibold text-white hover:bg-[#E85F00] sm:h-10 sm:px-4 sm:text-sm">
              <Link to={AUTH_ENTRY_PATH}>Get Started Free</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6A00]">For Trainers & Basketball Programs</p>
              <h1 className="mt-4 text-4xl font-bold leading-tight text-[#0B0B0B] sm:text-6xl">
                Stop Running Your Basketball Program Out of a Group Chat
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-zinc-600">
                Publish sessions, share one link, manage live rosters and waitlists, and keep players updated from one place.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild className="h-12 rounded-xl bg-[#FF6A00] px-7 text-base font-semibold text-white hover:bg-[#E85F00]">
                  <Link to={AUTH_ENTRY_PATH}>Create Your First Session</Link>
                </Button>
                {audienceCta.to ? (
                  <Button asChild variant="outline" className="h-12 rounded-xl border-zinc-300 px-7 text-base font-semibold">
                    <Link
                      to={audienceCta.to}
                      onClick={audienceCta.to === TRAINER_FEATURES_HASH ? () => scrollToTrainerFeatures() : undefined}
                      aria-describedby={audienceCta.description ? 'trainer-audience-cta-status' : undefined}
                    >
                      {audienceCta.label}
                    </Link>
                  </Button>
                ) : (
                  <Button type="button" variant="outline" className="h-12 rounded-xl border-zinc-300 px-7 text-base font-semibold" disabled>
                    {audienceCta.label}
                  </Button>
                )}
              </div>
              {audienceCta.description && (
                <p id="trainer-audience-cta-status" className="mt-3 max-w-2xl text-sm leading-6 text-zinc-600" aria-live="polite">
                  {audienceCta.description}
                </p>
              )}
            </div>

            <div className="rounded-3xl bg-zinc-950 p-7 text-white shadow-xl sm:p-9">
              <p className="text-sm font-bold uppercase tracking-widest text-orange-400">Everything your group chat can’t manage</p>
              <ul className="mt-6 space-y-4">
                {[
                  'One shareable session link',
                  'Live rosters, waitlists, and invitations',
                  'Player updates and schedule changes',
                  'Recurring sessions and game-day check-in',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-orange-400" />
                    <span className="leading-6 text-zinc-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="trainer-features" className="scroll-mt-52 bg-white px-4 py-16 sm:scroll-mt-40 sm:px-6 sm:py-20 lg:px-8" aria-labelledby="trainer-capabilities">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6A00]">From setup to game day</p>
              <h2 id="trainer-capabilities" className="mt-3 text-3xl font-bold text-[#0B0B0B] sm:text-4xl">
                From first share to final check-in
              </h2>
              <p className="mt-4 text-zinc-600">Create the session once, keep one link current, and manage what happens next from the same workflow.</p>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {CAPABILITIES.map(({ icon: Icon, title, description }) => (
                <article key={title} className="rounded-2xl border border-zinc-200 p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-100">
                    <Icon className="h-5 w-5 text-[#FF6A00]" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-[#0B0B0B]">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-zinc-600">{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 sm:py-20 lg:px-8" aria-labelledby="trainer-public-pages">
          <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-2">
            <div className="rounded-3xl border border-orange-200 bg-orange-50 p-7">
              <Eye className="h-7 w-7 text-[#FF6A00]" />
              <h2 id="trainer-public-pages" className="mt-4 text-2xl font-bold text-[#0B0B0B]">Give players one page to trust</h2>
              <p className="mt-3 leading-7 text-zinc-700">
                Eligible trainers can share a public storefront and session pages with approved details. Private roster and contact data stay off those public pages.
              </p>
            </div>
            <div className="rounded-3xl border border-zinc-200 bg-white p-7">
              <ShieldCheck className="h-7 w-7 text-emerald-600" />
              <h2 className="mt-4 text-2xl font-bold text-[#0B0B0B]">Youth privacy stays in force</h2>
              <ul className="mt-3 space-y-3 leading-7 text-zinc-700">
                <li className="flex gap-3"><LockKeyhole className="mt-1 h-5 w-5 shrink-0 text-zinc-500" />Contact information for minors is never displayed on public trainer or session pages.</li>
                <li className="flex gap-3"><LockKeyhole className="mt-1 h-5 w-5 shrink-0 text-zinc-500" />Youth-sensitive locations follow the platform’s existing public-location redaction rules.</li>
              </ul>
              <p className="mt-4 text-sm text-zinc-500">
                OpenRuns does not claim identity verification, background screening, or guaranteed safety outcomes.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-zinc-950 px-4 py-16 text-center text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl">
            <h2 className="text-3xl font-bold">Ready to organize your first session?</h2>
            <p className="mt-4 text-zinc-300">Publish the details, share one link, and manage what changes from the same place.</p>
            <Button asChild className="mt-8 h-12 rounded-xl bg-[#FF6A00] px-8 text-base font-semibold text-white hover:bg-[#E85F00]">
              <Link to={AUTH_ENTRY_PATH}>Create Your First Session</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TrainerPrograms;
